import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2 } from 'lucide-react';

const FreightCertificatePrint = forwardRef(({ docketId, formData, companyData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [loading, setLoading] = useState(!companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    const fetchCompany = async () => {
      if (companyData) { setCompany(companyData); setLoading(false); return; }
      try {
        const dr = await fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuthHeaders() });
        if (dr.ok) {
          const dd = await dr.json();
          const cr = await fetch(`${backendUrl}/api/companies/${dd.company_id}`, { headers: getAuthHeaders() });
          if (cr.ok) setCompany(await cr.json());
        }
      } catch (e) { console.error('Failed to fetch company', e); }
      finally { setLoading(false); }
    };
    fetchCompany();
  }, [docketId, companyData, backendUrl]);

  useImperativeHandle(ref, () => ({ print: () => window.print() }));

  const fmt = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return d; }
  };

  const fmtPair = (val, date) => val && date ? `${val} / ${fmt(date)}` : (val || '');

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const companyName    = company?.name        || 'COMPANY NAME';
  // Convert Drive URL to thumbnail URL to avoid CORS/403 issues
  const getLogoUrl = (url) => {
    if (!url) return null;
    const fileId = url.match(/id=([^&]+)/)?.[1] || url.match(/\/d\/([^/]+)\//)?.[1];
    if (fileId) return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
    return url;
  };
  const companyLogo    = getLogoUrl(company?.logo_url);
  const companyAddress = [company?.address, company?.city, company?.state, company?.pincode].filter(Boolean).join(', ');
  const companyPhone   = company?.phone       || '';
  const companyEmail   = company?.email       || '';
  const companyWebsite = company?.website     || '';
  const companyGst     = company?.gst_number  || '';
  const companyCin     = company?.cin_number  || '';

  const shipmentRows = [
    { label: 'Flight Name',              value: formData.airline || formData.flight_name || '' },
    { label: 'Flight No. / Date',        value: fmtPair(formData.flight_no, formData.flight_date) },
    { label: 'Airport of Load',          value: formData.airport_of_load || '' },
    { label: 'Airport of Discharge',     value: formData.airport_of_discharge || '' },
    { label: 'MAWB No. / Date',          value: fmtPair(formData.mawb_no, formData.mawb_date) },
    { label: 'No of Packages MAWB',      value: formData.mawb_packages || '' },
    { label: 'Gr.wt. MAWB',             value: formData.mawb_gross_weight || '' },
    { label: 'Description MAWB',         value: formData.mawb_description || '' },
    { label: 'HAWB No. / Date',          value: fmtPair(formData.hawb_no, formData.hawb_date) },
    { label: 'No of Packages HAWB',      value: formData.hawb_packages || '' },
    { label: 'Gr.wt. HAWB',             value: formData.hawb_gross_weight || '' },
    { label: 'Description HAWB',         value: formData.hawb_description || '' },
    { label: 'Temp',                     value: formData.temperature || '' },
  ];

  return (
    <>
      <div
        id="fc-print-content"
        className="bg-white print:bg-white"
        style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.4' }}
      >
        <div className="p-6 print:p-6 relative">

          {/* Watermark */}
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
            style={{ zIndex: 0 }}
          >
            <div
              className="font-bold text-center whitespace-nowrap transform -rotate-45"
              style={{ fontSize: '55px', opacity: 0.05, letterSpacing: '8px', color: '#555' }}
            >
              {companyName}
            </div>
          </div>

          {/* Content */}
          <div className="relative" style={{ zIndex: 1 }}>

            {/* ── Header ── */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-shrink-0">
                {companyLogo ? (
                  <img src={companyLogo} alt={companyName} className="h-16 w-auto object-contain" />
                ) : (
                  <div className="h-16 w-20 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">LOGO</div>
                )}
              </div>
              <div className="text-right text-xs">
                <p className="font-semibold">Freight Forwarder</p>
                <p className="font-semibold">Custom Broker</p>
                <p className="font-semibold">Reefer Transportation</p>
                <p className="font-semibold">Logistics Solution</p>
                {companyWebsite && <p className="mt-1 text-blue-600">{companyWebsite}</p>}
              </div>
            </div>

            <div className="border-t-2 border-black mb-3"></div>

            {/* ── Title ── */}
            <div className="text-center mb-2">
              <h1 className="text-base font-bold">FREIGHT CERTIFICATE</h1>
            </div>

            {/* ── Job No / Date ── */}
            <div className="flex justify-between mb-3 text-xs">
              <div><span className="font-semibold">Job No.: </span>{formData.job_no || ''}</div>
              <div><span className="font-semibold">Date: </span>{fmt(formData.date)}</div>
            </div>

            {/* ── Shipper / Consignee Table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-3">
              <thead>
                <tr>
                  <th className="border border-black p-2 text-left font-bold w-1/2 bg-gray-50">SHIPPER</th>
                  <th className="border border-black p-2 text-left font-bold w-1/2 bg-gray-50">CONSIGNEE</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black p-2 align-top">
                    <div className="whitespace-pre-wrap text-[10px] min-h-[60px]">{formData.shipper || ''}</div>
                  </td>
                  <td className="border border-black p-2 align-top">
                    <div className="whitespace-pre-wrap text-[10px] min-h-[60px]">{formData.consignee || ''}</div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ── Shipment Details Table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-3">
              <tbody>
                {shipmentRows.map((row) => (
                  <tr key={row.label}>
                    <td className="border border-black p-1.5 font-semibold bg-gray-50" style={{ width: '35%', whiteSpace: 'nowrap' }}>
                      {row.label}
                    </td>
                    <td className="border border-black p-1.5">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Freight Details Table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-2">
              <thead>
                <tr>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50" style={{ width: '40%' }}>Description</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Qty</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Currency</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Amount</th>
                  <th className="border border-black p-1.5 text-left font-bold bg-gray-50">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black p-1.5 align-top">
                    <div className="whitespace-pre-wrap min-h-[40px]">{formData.description || ''}</div>
                  </td>
                  <td className="border border-black p-1.5 align-top">{formData.quantity || ''}</td>
                  <td className="border border-black p-1.5 align-top">{formData.currency || ''}</td>
                  <td className="border border-black p-1.5 align-top">{formData.amount || ''}</td>
                  <td className="border border-black p-1.5 align-top">{formData.total_amount || ''}</td>
                </tr>
              </tbody>
            </table>

            {/* ── Note ── */}
            <div className="mb-3 text-[9px] text-gray-700">
              <span className="font-semibold">Note : </span>
              Subject to GST, Freight certificate is not the Invoice. Invoice will be issued separately after completion of the Shipment.
            </div>

            {/* ── Signature ── */}
            <div className="flex justify-end mb-4 mt-4">
              <div className="text-right text-xs">
                <p className="font-semibold mb-8">For, {companyName}</p>
                <div className="border-t border-black pt-1 w-44">
                  <p className="text-center">Authorized Signatory</p>
                </div>
              </div>
            </div>

            {/* ── Footer ── */}
            <div id="fc-footer" className="mt-4">
              <div className="border-t-2 border-black mb-2"></div>
              <div className="text-center" style={{ fontSize: '9px', lineHeight: '1.6' }}>
                <p style={{ fontWeight: 'bold', letterSpacing: '0.05em' }}>{companyName}</p>
                <p>
                  {companyAddress && (
                    <><span style={{ fontWeight: 'bold' }}>Reg Off: </span><span>{companyAddress}</span></>
                  )}
                  {companyAddress && companyPhone && <span>  </span>}
                  {companyPhone && (
                    <><span style={{ fontWeight: 'bold' }}>Tel: </span><span>{companyPhone}</span></>
                  )}
                  {companyPhone && companyGst && <span>  </span>}
                  {companyGst && (
                    <><span style={{ fontWeight: 'bold' }}>GST No.: </span><span>{companyGst}</span></>
                  )}
                  {companyGst && companyEmail && <span>  </span>}
                  {companyEmail && (
                    <><span style={{ fontWeight: 'bold' }}>Email: </span><span>{companyEmail}</span></>
                  )}
                  {companyEmail && companyCin && <span>  </span>}
                  {companyCin && (
                    <><span style={{ fontWeight: 'bold' }}>LLP IN No.: </span><span>{companyCin}</span></>
                  )}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }

          body * { visibility: hidden; }

          #fc-print-content, #fc-print-content * { visibility: visible; }

          #fc-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            min-height: calc(297mm - 20mm);
            display: flex !important;
            flex-direction: column !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          #fc-print-content > div {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }

          #fc-print-content > div > div.relative {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }

          #fc-footer { margin-top: auto !important; }

          table, td, th { border-color: black !important; }

          .bg-gray-50 {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact !important;
          }
        }

        @media screen {
          #fc-print-content {
            max-width: 210mm;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>
    </>
  );
});

FreightCertificatePrint.displayName = 'FreightCertificatePrint';

export default FreightCertificatePrint;
